import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";
import Img from "../Img/AccR.jpg";
import {
  Book,
  Calculator,
  FileText,
  BarChart2,
  Banknote,
  Building2,
  Briefcase,
  ChevronDownCircle,
} from "lucide-react";

export default function Accounting() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    window.scrollTo(0, 0);
  }, []);

  const curriculum = [
    {
      title: "Basics & Fundamentals",
      description:
        "Learn the core principles of accounting including financial statements, bookkeeping, and accounting standards.",
      icon: Book,
    },
    {
      title: "Advanced Concepts",
      description:
        "Master advanced topics like cost accounting, auditing, taxation, and compliance.",
      icon: FileText,
    },
    {
      title: "Practical Applications",
      description:
        "Work on real-life case studies and projects to understand practical business scenarios.",
      icon: Calculator,
    },
    {
      title: "Software & Tools",
      description:
        "Hands-on training on accounting software like Tally, QuickBooks, and Excel.",
      icon: BarChart2,
    },
    {
      title: "Industry Standards",
      description:
        "Understand industry best practices, IFRS, and GAAP compliance.",
      icon: Banknote,
    },
    {
      title: "Project Work",
      description:
        "Collaborate on accounting projects simulating real business environments.",
      icon: FileText,
    },
    {
      title: "Analysis & Reports",
      description:
        "Learn to analyze financial data and prepare management reports.",
      icon: BarChart2,
    },
    {
      title: "Final Project Showcase",
      description:
        "Present a complete accounting project demonstrating learned skills.",
      icon: Building2,
    },
  ];

  const careers = [
    {
      title: "Accounting Specialist",
      description:
        "Manage day-to-day accounting operations, ensure accurate records, and support financial reporting.",
    },
    {
      title: "Finance Operator",
      description:
        "Handle cash flows, payroll, and budgeting processes in organizations.",
    },
    {
      title: "Business Consultant",
      description:
        "Advise companies on financial decisions, cost optimization, and performance improvement.",
    },
    {
      title: "Executive Officer",
      description:
        "Oversee financial operations, compliance, and strategic financial planning.",
    },
    {
      title: "Finance Assistant",
      description:
        "Assist in financial analysis, reporting, and administrative tasks in accounting teams.",
    },
  ];

  const faqs = [
    {
      question: "Is this course suitable for beginners?",
      answer:
        "Yes, we start from the fundamentals. No prior experience needed.",
    },
    {
      question: "Will I learn practical tools?",
      answer:
        "Absolutely! You’ll master industry-relevant software and techniques.",
    },
    {
      question: "Do you provide placement assistance?",
      answer: "Yes, we offer job referrals and interview preparation support.",
    },
  ];

  return (
    <div className="bg-gray-50 text-gray-800 overflow-hidden">
      {/* Hero */}
      <section
        className="relative flex items-center justify-center text-center text-white"
        style={{
          minHeight: "70vh",
          background: `url(${require("../Img/Acc-img.jpg")}) center/cover no-repeat`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-transparent"></div>
        <div className="relative z-10 max-w-3xl px-6" data-aos="fade-up">
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-lime-200">
            Accounting Course
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed">
            Master financial management & decision-making through hands-on
            projects and real-world case studies.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-block rounded-full px-8 py-3 text-lg font-semibold shadow-lg bg-gradient-to-r from-green-500 to-emerald-400 hover:from-emerald-500 hover:to-green-600 transition-transform transform hover:scale-105 animate-bounce"
          >
            🚀 Enroll Now
          </Link>
        </div>
      </section>

      {/* Why Learn */}
      <section className="py-16 bg-gradient-to-b from-white via-green-50 to-white">
        <div className="container mx-auto px-6 lg:px-12">
          <h2
            className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12"
            data-aos="fade-up"
          >
            Why Learn This?
          </h2>

          <div className="flex flex-col items-center text-center gap-10">
            {/* Image */}
            <img
              src={Img}
              alt="Accounting"
              className="rounded-2xl shadow-xl max-w-md mx-auto"
              data-aos="zoom-in"
            />

            {/* Content Points in 2 Columns with Icon Animation */}
            <ul className="grid md:grid-cols-2 gap-6 w-full max-w-4xl">
              {[
                {
                  icon: <Book />,
                  title: "Build essential industry knowledge",
                  description:
                    "Learn the core principles of accounting, bookkeeping, and financial statements. Develop a strong foundation that will guide your career in finance.",
                },
                {
                  icon: <Calculator />,
                  title: "Open high-paying opportunities",
                  description:
                    "Acquire skills that are in high demand in finance and business sectors. Increase your earning potential by becoming a proficient accounting professional.",
                },
                {
                  icon: <BarChart2 />,
                  title: "Learn modern tools & techniques",
                  description:
                    "Get hands-on experience with accounting software like Tally, QuickBooks, and Excel. Understand modern financial analysis techniques used in industry.",
                },
                {
                  icon: <Banknote />,
                  title: "Apply skills in real-world scenarios",
                  description:
                    "Work on projects and case studies that simulate real business situations. Learn how to solve financial challenges effectively.",
                },
                {
                  icon: <FileText />,
                  title: "Stay updated with global standards",
                  description:
                    "Understand international accounting standards like IFRS and GAAP. Keep your skills relevant in a global finance environment.",
                },
                {
                  icon: <Briefcase />,
                  title: "Boost career prospects",
                  description:
                    "Prepare for a variety of finance roles, including accounting specialist, finance analyst, and business consultant. Gain confidence to take on professional responsibilities.",
                },
                {
                  icon: <Building2 />,
                  title: "Gain practical experience",
                  description:
                    "Collaborate on real-life accounting projects and case studies. Learn how to apply theoretical knowledge to practical business scenarios.",
                },
              ].map((item, idx) => (
                <li
                  key={idx}
                  className="flex flex-col items-start p-4 rounded-lg bg-white/80 shadow-md transition transform hover:scale-105 hover:shadow-lg hover:shadow-green-400/30"
                  data-aos="fade-up"
                  data-aos-delay={idx * 150}
                >
                  <div
                    className="flex items-center text-green-500 text-2xl mb-2 transition-transform transform hover:scale-125 hover:rotate-12 animate-bounce-slow"
                    data-aos="zoom-in"
                    data-aos-delay={idx * 200}
                  >
                    {item.icon}
                    <span className="ml-3 font-semibold text-base">
                      {item.title}
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm">{item.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-white">
        <div className="container mx-auto px-6">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800"
            data-aos="fade-up"
          >
            What You’ll Learn
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {curriculum.map((item, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-3 p-6 rounded-2xl bg-white shadow-md transition transform hover:scale-105 hover:shadow-lg hover:shadow-green-400/40"
                data-aos="fade-up"
                data-aos-delay={idx * 150}
              >
                {/* <div className="p-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-400 text-white transition-transform transform hover:scale-125 hover:rotate-12">
                  <item.icon size={28} />
                </div>
                <h6 className="text-lg font-semibold">{item.title}</h6> */}
                <div className="flex items-center gap-3 p-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-400 text-white transition-transform transform hover:scale-110 hover:rotate-1">
                  <item.icon size={24} />
                  <span className="text-base font-semibold">{item.title}</span>
                </div>
                <p className="text-gray-600">{item.description}</p>

                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section
        className="relative py-16 text-white"
        style={{
          background: `url(${require("../Img/Acc-img.jpg")}) center/cover fixed no-repeat`,
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10 container mx-auto px-6 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-12"
            data-aos="fade-up"
          >
            Career Paths After This Course
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {careers.map((career, index) => (
              <div
                key={index}
                className="p-6 bg-white text-gray-800 rounded-xl shadow-md transition transform hover:scale-105 hover:shadow-lg hover:shadow-green-400/40"
                data-aos="flip-left"
                data-aos-delay={index * 200}
              >
                {/* Icon with border, smaller size and bounce animation */}
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full border-2 border-green-500 text-green-600 transition-transform transform hover:scale-110 hover:rotate-6 animate-bounce-slow">
                  <Briefcase size={24} />
                </div>

                {/* Green Pill Bar with Career Title */}
                <div className="p-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-400 text-white text-sm font-semibold mb-4 inline-block">
                  {career.title}
                </div>

                {/* Description */}
                <p className="mt-2 text-gray-600 text-sm">
                  {career.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-6">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800"
            data-aos="fade-up"
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-6 max-w-3xl mx-auto">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="p-6 bg-white rounded-xl shadow-md transition transform hover:scale-105 hover:shadow-lg hover:shadow-green-400/30"
                data-aos="fade-right"
                data-aos-delay={idx * 200}
              >
                <details>
                  <summary className="flex items-center gap-2 cursor-pointer font-semibold text-lg">
                    <ChevronDownCircle className="text-green-500 transition-transform transform hover:rotate-90" />{" "}
                    {faq.question}
                  </summary>
                  <p className="mt-3 text-gray-600">{faq.answer}</p>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-500 to-emerald-400 text-center text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.toptal.com/designers/subtlepatterns/patterns/dot-grid.png')] opacity-10"></div>

        <div className="relative z-10 container mx-auto px-6">
          <h2 className="text-4xl font-bold mb-4" data-aos="fade-down">
            Begin Your Journey Today!
          </h2>
          <p className="text-lg mb-8 opacity-90" data-aos="fade-up">
            Get skilled, get certified, and land your dream job.
          </p>
          <Link
            to="/contact"
            className="inline-block px-6 py-2 bg-white text-green-600 font-bold rounded-lg shadow-lg hover:bg-gray-100 transition-transform transform hover:scale-105 text-sm"
            data-aos="zoom-in"
            data-aos-delay="300"
          >
            🚀 Enroll Now
          </Link>
        </div>
      </section>
    </div>
  );
}
